function readStringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** 飞书单元格内嵌图片（embed-image）的 fileToken */
export function extractFeishuImageFileToken(cell: unknown): string | null {
  if (!cell || typeof cell !== "object") return null;
  const obj = cell as Record<string, unknown>;
  if (readStringField(obj.type) !== "embed-image") return null;
  const fileToken = readStringField(obj.fileToken);
  return fileToken || null;
}

export function buildFeishuMediaProxyUrl(fileToken: string): string {
  return `/api/feishu-media/${encodeURIComponent(fileToken)}`;
}

function readSegmentTexts(value: unknown): string {
  if (!Array.isArray(value)) return "";
  return value
    .map((segment) => {
      if (!segment || typeof segment !== "object") return "";
      return readStringField((segment as { text?: unknown }).text);
    })
    .join("");
}

/**
 * 将飞书表格单元格值转为可展示文本。
 * 复杂类型（链接、@人、公式等）在 API 中可能以对象返回。
 *
 * 单元格数组是富文本片段（多段文字、链接、换行），按原文直接拼接；
 * 换行已包含在片段 text 中。下拉多选走 multipleValue，仍用逗号分隔。
 */
export function formatFeishuCellValue(cell: unknown): string {
  if (cell == null) return "";

  if (typeof cell === "string") return cell;
  if (typeof cell === "number" || typeof cell === "boolean") return String(cell);

  if (Array.isArray(cell)) {
    return cell.map((item) => formatFeishuCellValue(item)).join("");
  }

  if (typeof cell !== "object") return String(cell);

  const obj = cell as Record<string, unknown>;
  const type = readStringField(obj.type);

  if (type === "url") {
    const text = readStringField(obj.text) || readSegmentTexts(obj.texts);
    const link = readStringField(obj.link);
    if (text && link && text !== link) return text;
    return text || link;
  }

  if (type === "mention") {
    const text = readStringField(obj.text);
    return text ? `@${text}` : "";
  }

  if (type === "formula") {
    return readStringField(obj.text);
  }

  if (type === "embed-image") {
    return "[图片]";
  }

  if (type === "multipleValue") {
    const values = Array.isArray(obj.values) ? obj.values : [];
    return values
      .map((value) => formatFeishuCellValue(value))
      .filter(Boolean)
      .join(", ");
  }

  const segmentText = readSegmentTexts(obj.texts);
  if (segmentText) return segmentText;

  const text = readStringField(obj.text);
  if (text) return text;

  const link = readStringField(obj.link);
  if (link) return link;

  try {
    return JSON.stringify(cell);
  } catch {
    return String(cell);
  }
}

export function normalizeFeishuCellValues(values: unknown[][] | undefined): string[][] {
  if (!values?.length) return [];
  return values.map((row) => (row ?? []).map((cell) => formatFeishuCellValue(cell)));
}

/** 判断单元格是否为需 JSON 展示的复杂对象 */
export function isFeishuComplexCell(cell: unknown): boolean {
  return cell != null && typeof cell === "object";
}
